<template>
  <button
    class="btn"
    :class="{
      'btn--loading': loading,
      ['btn--variant-' + variant]: variant,
      ['btn--size-' + size]: size,
      'btn--icon-only': isIconOnly,
    }"
    @click="handleClick"
    :disabled="isDisabled || loading"
  >
    <LoadingSpinner v-if="loading" color="light" />
    <div class="btn__content" :class="{ 'btn__content--icon-left': iconPosition === 'left' }">
      <BaseIcon v-if="icon && iconPosition === 'left'" :name="icon" size="12" />
      <span>
        <slot>{{ text }}</slot>
      </span>
      <BaseIcon v-if="icon && iconPosition === 'right'" :name="icon" size="12" />
    </div>
  </button>
</template>

<script>
  import { computed } from 'vue';
  import LoadingSpinner from '@shared/components/LoadingSpinner.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  export default {
    name: 'ButtonEl',
    props: {
      text: {
        type: String,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: String,
      },
      iconPosition: {
        type: String,
        default: 'right',
        validator: (value) => ['left', 'right'].includes(value),
      },
      variant: {
        type: String,
        default: 'primary',
      },
      size: {
        type: String,
        default: 'md',
        validator: (value) => ['xs', 'sm', 'md', 'lg'].includes(value),
      },
    },
    components: {
      LoadingSpinner,
      BaseIcon,
    },
    emits: ['click'],
    setup(props, { emit, slots }) {
      const isDisabled = computed(() => (props.loading || props.disabled ? true : false));
      const hasSlot = computed(() => !!slots.default);
      const isIconOnly = computed(() => !props.text && !hasSlot && !!props.icon);
      const handleClick = (event) => {
        emit('click', event);
      };
      return {
        isDisabled,
        handleClick,
        isIconOnly,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@shared/assets/css/base/mixins.scss';

  .btn {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    padding-top: 0.625rem;
    padding-bottom: 0.625rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    border-width: 1px;
    border-color: transparent;
    border-style: solid;
    font-size: 0.875rem;
    line-height: 1.5;
    font-weight: 500;
    box-shadow:
      0 0 #0000,
      0 0 #0000,
      0 1px 2px 0 rgb(0 0 0 / 0.05);
    background-color: var(--cs-button-default-background, #3b82f6);
    color: var(--cs-button-default-text, #ffffff);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    &:hover {
      background-color: var(--cs-button-hover-background, #2563eb);
      color: var(--cs-button-hover-text, #ffffff);
    }
    &__content {
      transition: padding 150ms ease-out;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      &--icon-left {
        flex-direction: row;
        .icon {
          margin-left: 0;
          margin-right: 0.5rem;
        }
      }
    }
    &--loading {
      .btn__content {
        padding-left: 0.5rem;
      }
      opacity: 0.8;
      pointer-events: none;
    }
    &--icon-only {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      .btn__content {
        gap: 0;
      }
    }
    &:disabled {
      opacity: 0.8;
      pointer-events: none;
    }

    &--variant {
      &-outlined {
        background-color: transparent;
        border-color: var(--cs-input-default-border, #e2e8f0);
        color: var(--cs-input-default-text, #475569);
        @include hover {
          background-color: var(--cs-input-default-background, #f1f5f9);
          color: var(--cs-input-default-text, #475569);
          border-color: var(--cs-choice-hover-border, #3b82f6);
        }
      }
      &-secondary {
        background-color: var(--cs-button-secondary-background, #1e293b);
        color: var(--cs-button-secondary-text, #f1f5f9);
        @include hover {
          background-color: var(--cs-button-secondary-background, #1e293b);
          color: var(--cs-button-secondary-text, #f1f5f9);
          opacity: 0.8;
        }
      }
      &-outline {
        background-color: transparent;
        border-color: var(--cs-input-default-border, #e2e8f0);
        color: var(--cs-input-default-text, #475569);
        @include hover {
          background-color: var(--cs-input-default-background, #f1f5f9);
          color: var(--cs-input-default-text, #475569);
          border-color: var(--cs-choice-hover-border, #3b82f6);
        }
      }
      &-success {
        background-color: var(--cs-success-500);
        color: #ffffff;
        @include hover {
          background-color: var(--cs-success-700);
          color: #ffffff;
        }
      }
      &-dashed {
        width: 100%;
        background-color: transparent;
        border: 2px dashed var(--cs-input-default-border, #e2e8f0);
        color: var(--cs-input-default-text, #64748b);
        box-shadow: none;
        @include hover {
          background-color: var(--slate-50, #f8fafc);
          border-color: var(--cs-choice-hover-border, #3b82f6);
          color: var(--cs-choice-hover-border, #3b82f6);
        }
      }
      &-plain {
        background-color: transparent;
        border-color: transparent;
        color: var(--cs-input-default-text, #475569);
        box-shadow: none;
        @include hover {
          background-color: var(--slate-100, #f1f5f9);
          color: var(--cs-input-default-text, #475569);
        }
      }
      &-tertiary {
        background-color: transparent;
        border-color: transparent;
        color: var(--cs-input-default-text, #475569);
        box-shadow: none;
        @include hover {
          background-color: var(--slate-100, #f1f5f9);
        }
      }
    }

    // Size variants
    &--size {
      &-xs {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
        line-height: 1.25;
      }
      &-sm {
        padding: 0.375rem 0.75rem;
        font-size: 0.8125rem;
        line-height: 1.375;
      }
      &-md {
        /* Default size - uses base button styles */
        padding: 0.625rem 0.75rem;
      }
      &-lg {
        padding: 0.75rem 1.25rem;
        font-size: 1rem;
        line-height: 1.5;
      }
    }
  }
</style>
